import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import getGuidesHardcoded, { mergeRemoteGuides } from '@/data/guidesSteps';
import i18n from '@/i18n';

// Module-level cache so we only hit /api/walkthroughs once per session.
// HelpDrawer + HelpGuide both consume it and share the result.
let _remoteCache = null;
let _fetchPromise = null;

async function fetchRemoteOnce(axiosAuth) {
  if (_remoteCache !== null) return _remoteCache;
  if (_fetchPromise) return _fetchPromise;
  _fetchPromise = (async () => {
    try {
      const r = await axiosAuth().get('/walkthroughs');
      _remoteCache = Array.isArray(r.data) ? r.data : [];
    } catch {
      _remoteCache = [];
    } finally {
      _fetchPromise = null;
    }
    return _remoteCache;
  })();
  return _fetchPromise;
}

/**
 * useGuides — merges hardcoded walkthrough definitions with any
 * admin-authored entries from /api/walkthroughs. Returns the same shape
 * the existing components consume.
 *
 * Auth-aware: filters by user.role.  Lang-aware: re-merges when language
 * changes. Cache resets per page reload — exposed `refresh()` lets the
 * Walkthrough CMS page invalidate it after admins save a new guide.
 */
export default function useGuides() {
  const { user, axiosAuth } = useAuth();
  const [guides, setGuides] = useState(() => getGuidesHardcoded(user?.role));
  const role = user?.role;
  const lang = i18n.language?.startsWith('ar') ? 'ar' : 'en';

  useEffect(() => {
    let mounted = true;
    fetchRemoteOnce(axiosAuth).then((remote) => {
      if (!mounted) return;
      const visible = (remote || []).filter(
        (g) => !g.roles || g.roles.length === 0 || g.roles.includes(role),
      );
      setGuides(mergeRemoteGuides(getGuidesHardcoded(role), visible, lang));
    });
    return () => { mounted = false; };
  }, [role, lang, axiosAuth]);

  return guides;
}

export function invalidateGuidesCache() {
  _remoteCache = null;
}
